import * as React from 'react';

import _ from 'underscore';

import {
    getHumanReadableDueUnit,
    getHumanReadableSingularDueUnit,
} from 'nutshell-core/date-time/delays';
import type {DueDateUnit as DelayUnit} from 'nutshell-core/types';

import {Button} from '../button/button';
import {RemoveButton} from '../button/remove-button/remove-button';
import {ButtonMover} from '../button-mover/button-mover';
import {ClockIcon} from '../icon';
import {SavedSettingToast} from '../saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from '../saved-setting-toast/use-manage-save-state';
import {Body} from '../typography/body';

import {DelayInput} from './delay-input';
import {EditDelayModal} from './edit-delay-modal';

import styles from './delay-bar.module.css';

type Props = {
    delay: {
        unit: DelayUnit;
        timespan: number;
    };
    recipientCountText?: string;
    onShowRecipientModal?: () => void | null | undefined;
    saveChanges: (unit: DelayUnit, timespan: number) => Promise<any>;
    isSmall?: boolean;
    isEditable: boolean;
    editActiveDelayConfirmationDialog?: React.ComponentType<any>;
    isActive?: boolean;
    editionId: string;
    editionName?: string;
    isFirstDelay?: boolean;
    removeFirstDelay?: () => Promise<any>;
};

export function DelayBar(props: Props) {
    const [isEditDelayModalOpen, setIsEditDelayModalOpen] = React.useState<boolean>(false);

    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const {toastState} = useManageSaveState(isSaving, 2000);

    const [selectedDelayUnit, setSelectedDelayUnit] = React.useState<DelayUnit>(props.delay.unit);
    const [inputValue, setInputValue] = React.useState<number>(props.delay.timespan);

    React.useEffect(() => {
        setSelectedDelayUnit(props.delay.unit);
        setInputValue(props.delay.timespan);
    }, [props.delay]);

    const {saveChanges} = props;

    const handleSave = React.useMemo(
        () =>
            // @ts-expect-error
            _.debounce((unit, value) => {
                setIsSaving(true);
                saveChanges(unit, value).then(() => {
                    setIsSaving(false);
                });
            }, 500),
        [saveChanges]
    );

    let recipientInfo;
    if (props.recipientCountText && props.onShowRecipientModal) {
        recipientInfo = (
            <ButtonMover size='normal' direction='right'>
                <Button variant='text' onClick={props.onShowRecipientModal}>
                    <p>{props.recipientCountText}</p>
                </Button>
            </ButtonMover>
        );
    } else if (props.recipientCountText) {
        recipientInfo = <p>{props.recipientCountText}</p>;
    }

    const explanatoryCopy = props.isFirstDelay
        ? 'after the start trigger'
        : 'after the previous message';

    // Option to delete is for first delay only, and not shown if there are queued recipients
    const canDeleteDelay = !recipientInfo && props.isEditable && props.isFirstDelay;

    return (
        <>
            {props.isSmall ? (
                <button
                    onClick={props.isEditable ? () => setIsEditDelayModalOpen(true) : () => {}}
                    disabled={!props.isEditable}
                    className={styles['delay-button']}
                >
                    <div className={styles['delay-container']}>
                        <ClockIcon size={20} />
                        {props.delay.timespan}{' '}
                        {props.delay.timespan === 1
                            ? getHumanReadableSingularDueUnit(props.delay.unit)
                            : getHumanReadableDueUnit(props.delay.unit)}
                    </div>
                </button>
            ) : (
                <div
                    className={
                        props.isFirstDelay ? styles['delay-bar--first-delay'] : styles['delay-bar']
                    }
                >
                    <div className={styles['flex-container']}>
                        <div className={styles['delay-items']}>
                            <div className={styles['clock-icon']}>
                                <ClockIcon size={28} />
                            </div>
                            <SavedSettingToast state={toastState}>
                                {props.isEditable &&
                                !props.editActiveDelayConfirmationDialog &&
                                !props.isActive ? (
                                    // Can edit inline
                                    <>
                                        <DelayInput
                                            onSelectChange={(newUnit) => {
                                                setSelectedDelayUnit(newUnit);
                                                handleSave(newUnit, inputValue);
                                            }}
                                            selectedOption={selectedDelayUnit}
                                            onInputChange={(newTimespan) => {
                                                setInputValue(newTimespan);
                                                handleSave(selectedDelayUnit, newTimespan);
                                            }}
                                            inputValue={inputValue}
                                        />
                                        <div className={styles['explanatory-copy']}>
                                            {explanatoryCopy}
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Body>
                                            {props.delay.timespan}{' '}
                                            {props.delay.timespan === 1
                                                ? getHumanReadableSingularDueUnit(props.delay.unit)
                                                : getHumanReadableDueUnit(props.delay.unit)}{' '}
                                            {explanatoryCopy}
                                        </Body>
                                        {props.isEditable ? (
                                            <Button
                                                variant='text-primary'
                                                size='big'
                                                onClick={() => {
                                                    setIsEditDelayModalOpen(true);
                                                }}
                                            >
                                                Edit delay
                                            </Button>
                                        ) : undefined}
                                    </>
                                )}
                            </SavedSettingToast>
                        </div>
                        {recipientInfo}
                        {canDeleteDelay ? (
                            <RemoveButton
                                onClick={() => {
                                    if (typeof props.removeFirstDelay === 'function') {
                                        props.removeFirstDelay();
                                    }
                                }}
                                tooltip='Remove first delay'
                            />
                        ) : undefined}
                    </div>
                </div>
            )}
            {isEditDelayModalOpen ? (
                <EditDelayModal
                    onModalClose={() => {
                        setIsEditDelayModalOpen(false);
                        // Reset any entered values back to initial state
                        setInputValue(props.delay.timespan);
                        setSelectedDelayUnit(props.delay.unit);
                    }}
                    selectedDelayUnit={selectedDelayUnit}
                    setSelectedDelayUnit={setSelectedDelayUnit}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    isSaving={isSaving}
                    onClearSaveComplete={() => {
                        setIsEditDelayModalOpen(false);
                    }}
                    saveChanges={() => {
                        setIsSaving(true);
                        saveChanges(selectedDelayUnit, inputValue).then(() => {
                            setIsSaving(false);
                        });
                    }}
                    editionId={props.editionId}
                    editionName={props.editionName ? props.editionName : 'this message'}
                    confirmationDialog={props.editActiveDelayConfirmationDialog}
                />
            ) : undefined}
        </>
    );
}
